import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useAuthtoolContext} from '@mia-platform-internal/authtool-client-js/react';
import profileImageService from '../services/profileImageService';
import AdkClient from '../AdkClient';
import Conversations from '../Conversations';
import { setAuthorizedFetch } from '../utils/fetchRegistry';

const AuthContext = createContext(null);

// sessionStorage key used to persist an auth hint across page refreshes
const SESSION_AUTH_HINT_KEY = 'mf_auth_hint';

/**
 * Authentication Context Provider
 * Wraps authtool-client-js and adds profile image management
 */
export function AuthProvider({children}) {
  const { user: authtoolUser, fetch: authorizedFetch, loginUrl, logoutUrl, baseUrl } = useAuthtoolContext();
  const [user, setUser] = useState(null);

  // Keep fetchRegistry in sync with authtool's authorized fetch so non-hook
  // code always uses the correct (token-bearing) fetch function.
  useEffect(() => {
    setAuthorizedFetch(authorizedFetch);
  }, [authorizedFetch]);

  // Optimistic session hint: if the user was authenticated in a previous page load,
  // skip the loading spinner while the real BFF check runs in the background.
  const hadSession = useRef(sessionStorage.getItem(SESSION_AUTH_HINT_KEY) === '1');
  const [isLoading, setIsLoading] = useState(() => !hadSession.current);

  // Derive authentication state from authtool user.
  // While the BFF check is still pending (authtoolUser === undefined) and we have a
  // cached session hint, treat the user as authenticated optimistically.
  const authCheckPending = authtoolUser === undefined;
  const isAuthenticated =
    (authtoolUser !== undefined && !('error' in authtoolUser)) ||
    (authCheckPending && hadSession.current);

  // Load profile image for user
  const loadProfileImage = useCallback(async (userInfo) => {
    if (!userInfo?.sub && !userInfo?.id) return userInfo;
    
    try {
      const hasImage = await profileImageService.hasProfileImage(userInfo.sub || userInfo.id);
      if (hasImage) {
        const imageUrl = profileImageService.getProfileImageUrl(userInfo.sub || userInfo.id);
        return { ...userInfo, photo: imageUrl };
      }
    } catch (error) {
      console.error('Failed to load profile image:', error);
    }
    
    return userInfo;
  }, []);

  // Update user state when authtool user changes
  useEffect(() => {
    const updateUser = async () => {
      console.log('[AuthContext] authtoolUser changed:', authtoolUser);
      
      // Case 1: User is authenticated
      if (authtoolUser && !('error' in authtoolUser)) {
        console.log('[AuthContext] User is authenticated');
        const userInfo = {
          ...authtoolUser.userinfo,
          valid_until: authtoolUser.valid_until
        };
        const enrichedUser = await loadProfileImage(userInfo);
        setUser(enrichedUser);
        setIsLoading(false);
        // Persist the session hint so the next page refresh is optimistic
        sessionStorage.setItem(SESSION_AUTH_HINT_KEY, '1');
        hadSession.current = true;
        // Sync the authenticated user's ID to AdkClient and Conversations
        const userId = userInfo.sub || userInfo.id || userInfo.email;
        AdkClient.setUserId(userId);
        Conversations.setUserId(userId);
      }
      // Case 2: User is not authenticated (got any error: 401, 404, 500, network error, etc.)
      else if (authtoolUser && 'error' in authtoolUser) {
        console.log('[AuthContext] User is NOT authenticated (error):', authtoolUser.error);
        // Clear the session hint so the next page load doesn't optimistically authenticate
        sessionStorage.removeItem(SESSION_AUTH_HINT_KEY);
        hadSession.current = false;
        setUser(null);
        setIsLoading(false);
        // Clear user ID in AdkClient and Conversations on logout/unauthenticated state
        AdkClient.setUserId(null);
        Conversations.setUserId(null);
      }
      // Case 3: Still loading (authtoolUser is undefined)
      else {
        console.log('[AuthContext] Still waiting for auth check (authtoolUser is undefined)');
      }
    };

    updateUser();
  }, [authtoolUser, loadProfileImage]);

  // Login method - redirect to login page
  const login = useCallback(() => {
    window.location.href = loginUrl(window.location.href).href
  }, [loginUrl]);

  // Logout method - redirect to logout endpoint
  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_AUTH_HINT_KEY);
    hadSession.current = false;
    window.location.href = logoutUrl(window.location.href).href
  }, [logoutUrl]);

  // Update user profile photo
  const updateUserPhoto = useCallback((photoUrl) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      return { ...prevUser, photo: photoUrl };
    });
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    updateUserPhoto,
    authorizedFetch, // Expose authorized fetch from authtool
    baseUrl,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
